import React, { useEffect, useState } from 'react';
import { BOAPI } from "../service/UsersService";

const MAX_LEN = 512;

// ניהול הטקסט המקדים של השיעורים — התיבה שמופיעה באפליקציה מתחת לסרגל הכלים במסך הלימוד.
// ריק = התיבה לא מוצגת באפליקציה.
const LessonDescriptionsTab = () => {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});     // id -> טקסט שנערך ועוד לא נשמר
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await BOAPI.fetchLessonDescriptions();
      if (Array.isArray(result) && result[0]?.[0]?.code === 0 && Array.isArray(result[1])) {
        setItems(result[1]);
        setDrafts({});
      } else {
        setError(result?.[0]?.[0]?.msg || 'שגיאה בטעינת הנתונים — ודאו שהרצתם את LessonDescriptionsBO.sql');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const currentText = (item) => (drafts[item.id] !== undefined ? drafts[item.id] : (item.description || ''));
  const isDirty = (item) => drafts[item.id] !== undefined && drafts[item.id].trim() !== (item.description || '').trim();

  const handleSave = async (item) => {
    const text = currentText(item).trim();
    setSavingId(item.id);
    setError(null);
    try {
      const resp = await BOAPI.updateLessonDescription(item.id, text);
      if (Array.isArray(resp) && resp[0]?.[0]?.code === 0) {
        setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, description: text || null } : x)));
        setDrafts((prev) => { const n = { ...prev }; delete n[item.id]; return n; });
        setSavedId(item.id);
        setTimeout(() => setSavedId((cur) => (cur === item.id ? null : cur)), 2500);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שגיאה בשמירה');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בשמירה');
    } finally {
      setSavingId(null);
    }
  };

  const filtered = search.trim()
    ? items.filter((i) => `${i.name} ${i.description || ''}`.includes(search.trim()))
    : items;

  if (loading) return <div style={s.center}>טוען שיעורים...</div>;

  return (
    <div style={s.container}>
      <div style={s.headerSection}>
        <h2 style={s.title}>טקסט מקדים בשיעורים</h2>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש שיעור..."
          style={s.search}
        />
      </div>
      <div style={s.hint}>
        הטקסט מופיע באפליקציה בתיבה מתחת לסרגל הכלים במסך הלימוד של השיעור. השאירו ריק כדי שהתיבה לא תוצג. השינוי חל מיד, בלי עדכון גרסה.
      </div>

      {error && (
        <div style={s.error}>
          ❌ {error}
          <button onClick={() => setError(null)} style={s.closeErrorButton}>✕</button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={s.noData}>לא נמצאו שיעורים</div>
      ) : (
        filtered.map((item) => {
          const text = currentText(item);
          const dirty = isDirty(item);
          return (
            <div key={item.id} style={s.card}>
              <div style={s.cardHeader}>
                <b style={s.lessonName}>{item.name}</b>
                <span style={s.lessonId}>#{item.id}</span>
              </div>
              <textarea
                value={text}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [item.id]: e.target.value }))}
                rows={3}
                maxLength={MAX_LEN}
                placeholder="(ללא טקסט מקדים — התיבה לא מוצגת)"
                style={s.textarea}
              />
              <div style={s.cardFooter}>
                <span style={s.counter}>{text.length}/{MAX_LEN}</span>
                {savedId === item.id && <span style={s.savedMsg}>✓ נשמר</span>}
                {dirty && (
                  <button
                    onClick={() => setDrafts((prev) => { const n = { ...prev }; delete n[item.id]; return n; })}
                    style={s.cancelButton}
                  >
                    ↺ בטל שינויים
                  </button>
                )}
                <button
                  onClick={() => handleSave(item)}
                  disabled={!dirty || savingId === item.id}
                  style={{ ...s.saveButton, opacity: !dirty || savingId === item.id ? 0.5 : 1 }}
                >
                  {savingId === item.id ? 'שומר...' : '💾 שמור'}
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

const s = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },
  search: { padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '220px' },
  hint: { color: '#777', fontSize: '13px', marginBottom: '18px' },

  card: { backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '8px', padding: '16px', marginBottom: '14px' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  lessonName: { color: '#1a1f36', fontSize: '16px' },
  lessonId: { color: '#aaa', fontSize: '12px' },
  textarea: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', resize: 'vertical', direction: 'rtl' },
  cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' },
  counter: { marginLeft: 'auto', color: '#999', fontSize: '12px' },
  savedMsg: { color: '#2e7d32', fontSize: '13px', fontWeight: 'bold' },
  saveButton: { padding: '8px 16px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  cancelButton: { padding: '8px 14px', backgroundColor: '#eee', color: '#555', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default LessonDescriptionsTab;
